import {
  SURVEY_CEC_COLOR_INTERVALS,
  SURVEY_CLAY_COLOR_INTERVALS,
  SURVEY_HUMUS_COLOR_INTERVALS,
  SURVEY_ORGANIC_CARBON_COLOR_INTERVALS,
  SURVEY_LAYER_CLASSIFICATION_COLORS,
  SURVEY_LAYER_CLASSIFICATION_KEYS,
  SURVEY_LAYER_COLORS,
  SURVEY_LAYER_SOIL_TYPE_COLORS,
  SURVEY_LAYERS,
} from '../../constants/SurveyLayers';
import ViewModeConstants from '../../constants/ViewModeConstants';
import { getLayerConfig } from '../../components/Prescription/PrescriptionUtils';
import NumberUtils from '../../helpers/NumberUtils';

import lerp from '@sunify/lerp-color';
import { getMinMaxValues, getSourceCategory } from '../../helpers/SurveyUtils';
import { SOURCE_CATEGORY } from '../../components/Prescription/PrescriptionJob';
import type { FieldLayer } from '../../constants/FieldLayers';
import { getYieldColorIntervals } from '../../constants/YieldLayer';
import {
  getActiveSurveyCountryCode,
  getSurveyLayerColorIntervals,
  getSurveyLayerClassifications,
  getSoilClassDefinition,
  isSoilClassLayer,
} from './country/SurveyCountryStrategy';

const tinycolor = require('tinycolor2');
/**
 * Constant defining how many buckets we will pre-generate
 * for the color scales used in the "raw" and the "normalized" images.
 * @type {number}
 */
export const BUCKET_COUNT = 7;

export type ColorScaleEntry = {
  min: number | string;
  max: number | string;
  color: string | { toString: () => string };
  contrast?: string;
  index?: number;
  label?: React$Node;
  normMax?: number;
};

export interface ColorStrategy {
  isDiscrete: boolean;
  tooltipDecimals: number;
  getColor(val: number): string;
  getBucket(val: number): Object;
  scale: Array<ColorScaleEntry>;
}

export function RainbowColoringStrategy(layer: FieldLayer, countryCode?: string): ColorStrategy {
  this.config = getLayerConfig(layer, countryCode);

  this.getColor = (val) => {
    // Normalize the raw value to percentages between 0 and 1, since this is how the color scale is defined
    const normVal = this.config.mapValue(val, 0, 1);
    // Find the bucket after the one actually containing the value to use the upper-bound color.
    const bucket = this.scale.find((bucket) => normVal <= bucket.min / 100);
    // Return the bucket color
    return bucket.color;
  };

  this.tooltipDecimals = 0;
  this.scale = [
    { min: 0, max: 10, color: '#020058', contrast: '#FFFFFF', index: 0 },
    { min: 10, max: 20, color: '#0F0B9C', contrast: '#FFFFFF', index: 1 },
    { min: 20, max: 30, color: '#0029FF', contrast: '#000000', index: 2 },
    { min: 30, max: 40, color: '#00FFFF', contrast: '#000000', index: 3 },
    { min: 40, max: 50, color: '#00C445', contrast: '#000000', index: 4 },
    { min: 50, max: 60, color: '#0DB300', contrast: '#000000', index: 5 },
    { min: 60, max: 70, color: '#D5F200', contrast: '#000000', index: 6 },
    { min: 70, max: 80, color: '#FFEE00', contrast: '#000000', index: 7 },
    { min: 80, max: 90, color: '#FF6100', contrast: '#000000', index: 8 },
    { min: 90, max: 100, color: '#FF0E00', contrast: '#000000', index: 9 },
    { min: 100, max: Number.MAX_VALUE, color: '#840000', contrast: '#FFFFFF', index: 10 },
  ];
}

export function SoilTypeColoringStrategy(): ColorStrategy {
  this.getColor = (val) => {
    const jb = Number(val).toFixed(0);
    const col = SURVEY_LAYER_SOIL_TYPE_COLORS[jb];
    return col || 'black';
  };

  this.tooltipDecimals = 0;
  this.scale = [...Object.keys(SURVEY_LAYER_SOIL_TYPE_COLORS)].map((jb, idx) => {
    const color = this.getColor(jb);
    return {
      min: jb,
      max: jb,
      color: color,
      contrast: tinycolor(color).isDark() ? 'white' : 'black',
      index: idx,
    };
  });
}

export function SoilTypeColoringStrategyFactory(soilTypes, colorMap): ColorStrategy {
  let classifications = {};
  if (soilTypes) {
    classifications = soilTypes.reduce((acc, cur) => ({ ...acc, [cur.soilType]: cur }), {});
  }

  this.getColor = (val) => {
    const key = Number(val).toFixed(0);
    if (soilTypes && classifications[key]) return classifications[key].color;
    if (colorMap && colorMap[key]) return colorMap[key];
    return 'black';
  };

  this.getLabel = (val) => {
    const key = Number(val).toFixed(0);
    if (soilTypes && classifications[key]) return classifications[key].label;
    return undefined;
  };

  this.tooltipDecimals = 0;

  this.scale = (soilTypes || Object.keys(colorMap || {})).map((entry, idx) => {
    const st = soilTypes ? entry.soilType : Number(entry);
    const color = this.getColor(st);
    const label = soilTypes ? entry.label : undefined;

    return {
      min: st,
      max: st,
      color,
      label,
      getLabel: () => label,
      contrast: tinycolor(color).isDark() ? 'white' : 'black',
      index: idx,
    };
  });
}

export function HumusColoringStrategy(countryCode?: string): ColorStrategy {
  return new DiscreteColoringStrategy(
    getSurveyLayerColorIntervals(
      SURVEY_LAYERS.HUMUS,
      SURVEY_HUMUS_COLOR_INTERVALS,
      countryCode || getActiveSurveyCountryCode()
    )
  );
}

export function OrganicCarbonColoringStrategy(countryCode?: string): ColorStrategy {
  return new DiscreteColoringStrategy(
    getSurveyLayerColorIntervals(
      SURVEY_LAYERS.ORGANIC_CARBON,
      SURVEY_ORGANIC_CARBON_COLOR_INTERVALS,
      countryCode || getActiveSurveyCountryCode()
    )
  );
}

export function CecColoringStrategy(countryCode?: string): ColorStrategy {
  return new DiscreteColoringStrategy(
    getSurveyLayerColorIntervals(
      SURVEY_LAYERS.CEC,
      SURVEY_CEC_COLOR_INTERVALS,
      countryCode || getActiveSurveyCountryCode()
    )
  );
}

export function ClayColoringStrategy(countryCode?: string): ColorStrategy {
  return new DiscreteColoringStrategy(
    getSurveyLayerColorIntervals(
      SURVEY_LAYERS.CLAY,
      SURVEY_CLAY_COLOR_INTERVALS,
      countryCode || getActiveSurveyCountryCode()
    )
  );
}

export function DiscreteColoringStrategy(colorIntervals): ColorStrategy {
  this.isDiscrete = true;
  this.tooltipDecimals = 1;

  this.getBucket = (val: Number) => {
    return (
      this.scale.find((bucket) => val >= bucket.min && val < bucket.max) ||
      this.scale[this.scale.length - 1]
    );
  };
  this.getColor = (val: Number) => {
    return this.getBucket(val).color;
  };

  this.scale = Object.keys(colorIntervals).map((color, idx) => {
    const interval = colorIntervals[color];

    return {
      min: interval[0],
      max: interval[1],
      color: color,
      contrast: tinycolor(color).isDark() ? 'white' : 'black',
      index: idx,
    };
  });
}

export function ClassificationColoringStrategy(
  layer: FieldLayer,
  viewMode: string,
  countryCode?: string
): ColorStrategy {
  this.config = getLayerConfig(layer, countryCode);
  this.tooltipDecimals = 2;

  const classifications = getSurveyLayerClassifications(
    layer,
    countryCode || getActiveSurveyCountryCode()
  );

  this.classifier = (value) => {
    if (!classifications) {
      return null;
    }

    if (viewMode === ViewModeConstants.OVERVIEW) {
      // Use 3 classes
      const middle = classifications[SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_OK];
      if (value < middle[0]) {
        return SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_LARGE;
      } else if (value >= middle[1]) {
        return SURVEY_LAYER_CLASSIFICATION_KEYS.SURPLUS_SUSPICIOUS;
      } else {
        return SURVEY_LAYER_CLASSIFICATION_KEYS.DEMAND_OK;
      }
    } else {
      // Use all classes
      let result = null;
      Object.keys(classifications).forEach((key) => {
        const minMax = classifications[key];
        if (minMax.length > 0) {
          if (value >= minMax[0] && value < minMax[1]) {
            result = key;
          }
        }
      });

      return result;
    }
  };

  this.getColor = (val) => {
    const classification = this.classifier(val);
    return SURVEY_LAYER_CLASSIFICATION_COLORS[classification];
  };
}

export function VariationsColoringStrategy(
  layer: FieldLayer,
  min: number,
  max: number,
  defaultStrategy: ColorStrategy,
  countryCode?: string
): ColorStrategy {
  this.config = getLayerConfig(layer, countryCode);
  this.mapper = (val) => NumberUtils.map(val, min, max, 0, 1);
  this.tooltipDecimals = 2;

  // divide the field span into buckets with equal size
  const buckets = defaultStrategy.scale.length;
  const span = max - min;
  const bucketSize = span / buckets;

  // map the default scale and keep everything but the min and max values (colors etc).
  this.scale = defaultStrategy.scale.map((bucket, idx) => {
    const bucketMin = Math.max(min + idx * bucketSize, min);
    const bucketMax = Math.min(bucketMin + bucketSize, max);

    return {
      ...bucket,
      min: bucketMin,
      max: bucketMax,
    };
  });

  this.getColor = (val) => {
    if (val >= min && val <= max) {
      // Normalize the raw value to the field min-max range (local variation).
      const fieldNormVal = this.mapper(val);
      // Then map the normalized value to the default value range.
      const normVal = this.config.reverseMapValue(fieldNormVal, 0, 1);
      // Delegate coloring to the default coloring strategy.
      return defaultStrategy.getColor(normVal);
    }
  };
}

export function DefaultColoringStrategy(layer: FieldLayer, countryCode?: string): ColorStrategy {
  this.config = getLayerConfig(layer, countryCode);
  this.baseColor = tinycolor('#FFFFFF');
  this.primaryColor = tinycolor(SURVEY_LAYER_COLORS[layer]);
  this.tooltipDecimals = 2;
  // Define the min and max values for the color range
  const minValue = this.config.minValue ? this.config.minValue : 0;
  const maxValue = this.config.maxValue ? this.config.maxValue : 0;
  const buckets = BUCKET_COUNT;

  const stepSize = (maxValue - minValue) / (buckets - 1);

  // Pre-generate the color scale buckets
  this.scale = [...Array(buckets - 2)].map((_, i) => {
    const step = minValue + i * stepSize;

    // Normalize the current step value
    const normMax = this.config.mapValue(step, 0, 1);

    // Interpolate between min and max colors based on the normalized value
    const col = lerp(this.baseColor.toString(), this.primaryColor.toString(), normMax);
    const contrast = tinycolor(col).isDark() ? 'white' : 'black';

    // Pack up the bucket
    return {
      normMax: normMax,
      min: step,
      max: step + stepSize,
      color: col,
      contrast,
      index: i,
    };
  });

  const lastBucket = {
    normMax: 1.0,
    min: this.scale[this.scale.length - 1].max,
    max: Number.MAX_VALUE,
    color: this.primaryColor.toString(),
    contrast: this.primaryColor.isDark() ? 'white' : 'black',
  };

  this.scale.push(lastBucket);

  // Get the bucket for a given value
  this.getBucket = (val: Number) => {
    return this.scale.find((bucket) => val <= bucket.normMax);
  };
  this.getColor = (val: number) => {
    // Normalize the raw value to percentages between 0 and 1, since this is how the color scale is defined
    const normVal = this.config.mapValue(val, 0, 1);
    // Find the bucket after the one actually containing the value to use the upper-bound color.
    const bucket = this.getBucket(normVal);
    // Return the bucket color
    return bucket.color;
  };
}

export function YieldColoringStrategy(colorIntervals): ColorStrategy {
  this.isDiscrete = true;
  this.tooltipDecimals = 2;

  this.getBucket = (val: Number) => {
    return this.scale.find((bucket) => val >= bucket.min && val < bucket.max);
  };
  this.getColor = (val: Number) => {
    return this.getBucket(val).color;
  };

  this.scale = colorIntervals.map((i, idx) => {
    const interval = [i.min, i.max];
    const color = tinycolor({ r: i.r, g: i.g, b: i.b });

    return {
      min: interval[0],
      max: interval[1],
      color: color,
      contrast: tinycolor(color).isDark() ? 'white' : 'black',
      index: idx,
    };
  });
}

export const getColoringStrategy = (
  layer: FieldLayer,
  viewMode: string,
  classificationsEnabled: boolean,
  variationsEnabled: boolean,
  values: Array<Array<number>>,
  countryCode?: string,
  yieldColorIntervals?: Array<Object>
): ColorStrategy => {
  const category = getSourceCategory(layer);
  let defaultStrategy = new DefaultColoringStrategy(layer, countryCode);
  try {
    if (isSoilClassLayer(layer)) {
      const activeCountryCode = countryCode || getActiveSurveyCountryCode();
      const definition = getSoilClassDefinition(layer);
      if (definition) {
        return new SoilTypeColoringStrategyFactory(definition.soilTypes, definition.colorMap);
      }
    }

    if (category === SOURCE_CATEGORY.NUTRIENT) {
      if (classificationsEnabled) {
        return new ClassificationColoringStrategy(layer, viewMode, countryCode);
      }
    }

    if (category === SOURCE_CATEGORY.TEXTURE) {
      if (layer === SURVEY_LAYERS.HUMUS || layer === SURVEY_LAYERS.OM) {
        defaultStrategy = new HumusColoringStrategy(countryCode);
      } else if (layer === SURVEY_LAYERS.ORGANIC_CARBON) {
        defaultStrategy = new OrganicCarbonColoringStrategy(countryCode);
      } else if (layer === SURVEY_LAYERS.CEC) {
        defaultStrategy = new CecColoringStrategy(countryCode);
      } else if (layer === SURVEY_LAYERS.CLAY) {
        defaultStrategy = new ClayColoringStrategy(countryCode);
      } else {
        defaultStrategy = new RainbowColoringStrategy(layer, countryCode);
      }
    }

    if (category === SOURCE_CATEGORY.TOPOGRAPHY) {
      defaultStrategy = new RainbowColoringStrategy(layer, countryCode);
    }

    const activeYieldColorIntervals = yieldColorIntervals || getYieldColorIntervals();
    if (category === SOURCE_CATEGORY.YIELD && activeYieldColorIntervals) {
      defaultStrategy = new YieldColoringStrategy(activeYieldColorIntervals);
    }

    if (variationsEnabled) {
      if (!values || values.length === 0) {
        return defaultStrategy;
      }

      const { min, max } = getMinMaxValues(values);
      return new VariationsColoringStrategy(layer, min, max, defaultStrategy, countryCode);
    } else return defaultStrategy;
  } catch (e) {
    console.error('Failed to create color strategy for layer', layer);
    console.error(e);
  }

  return defaultStrategy;
};
